import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import xss from "xss";
import classNames from "classnames";
import { CircleAlert, MapPin, PackageX, ReceiptText, Send, Tag } from "lucide-react";
import { backendLink, backendLinkComplaint } from "../config";
import MetaTags from "../components/MetaTagsReclamation";
import { usePwaInstall } from "../components/hooks/usePwaInstall";
import {
  styles,
  Drawer,
  EMPTY_LOT,
  ErrorBox,
  Field,
  LocationFields,
  LotFields,
  MessageBox,
  MessageLabel,
  NextButton,
  PageShell,
  ProductPicker,
  Question,
  Reveal,
  ThanksScreen,
  TypeGrid,
  asFile,
  firstMissing,
  isEmpty,
  isValidEmail,
  localized,
  locationNames,
  lotSummary,
  parseDate,
  parseHour,
  toDigits,
  useDrawers,
  useLocations,
  LOT_SKIPPED,
  flagField,
} from "../components/wizard";

// Obligatory fields checked when leaving each step; the others are checked on submit
const STEP_FIELDS = {
  product: ["product_id"],
  problem: ["type_complaints"],
  lot: ["n_lot", "fabrication_date", "date_hour"],
  achat: ["willaya", "commune", "market_name"],
};

// One rule per obligatory field sent by the tenant config; true = filled
const FIELD_RULES = {
  email: (d) => !isEmpty(d.mail),
  full_name: (d) => !isEmpty(d.name),
  product_id: (d) => !!d.product,
  // the user can type the lot / date or send a picture of it
  fabrication_date: (d) => !(isEmpty(d.date) && d.compressedLot.length === 0),
  date_hour: (d) => !(isEmpty(d.date_hour) && d.compressedLot.length === 0),
  n_lot: (d) => !isEmpty(d.n_lot) || d.compressedLot.length > 0,
  phone: (d) => d.phone.length >= 9 && d.phone.length <= 10,
  type_complaints: (d) => d.selectedTypes.length > 0,
  willaya: (d) => !isEmpty(d.location.wilaya),
  commune: (d) => !isEmpty(d.location.commune),
  market_name: (d) => !isEmpty(d.location.market_name),
  img_r: (d) => d.compressedFiles.length > 0,
  voice_r: () => true,
  channel_id: () => true,
};

// Obligatory fields answered in the message box (text, photos, voice)
const MESSAGE_FIELDS = ["comment", "img_r", "voice_r"];

// Characters the previous form removed from the lot number
const LOT_FILTER = /[^a-zA-Z0-9 -_/,:]/g;

export default function ReclamationConsumer({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const navigate = useNavigate();
  const { isStandalone } = usePwaInstall();
  const utmSource = useSelector((state) => state.utm.source); // facebook, instagram, etc
  // scannerModule gates the camera scanner: tenants without it keep the plain 6-digit entry
  const tenantName = useSelector((state) => state.tenant?.tenant.name) || "";
  const enabledModules = useSelector((state) => state.tenant?.tenant.enabledModules) || {};
  const userFormFields = useSelector((state) => state.tenant.tenant.userFormFields) || [];
  const obligatoryUserFields = useSelector((state) => state.tenant.tenant.obligatoryUserFields) || [];
  const has = (field) => userFormFields.includes(field);
  const isRequired = (field) => obligatoryUserFields.includes(field);

  // synchronize language switch
  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  // ── Catalog data ────────────────────────────────────────────────────────────

  const [productsList, setProductsList] = useState([]);
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const locations = useLocations();

  useEffect(() => {
    axios
      .get(`${backendLink}shared/getAllProduct`)
      .then((response) => setProductsList(response.data.data.products || []))
      .catch((error) => console.error("Error fetching products:", error));
  }, []);

  // ── Form state ──────────────────────────────────────────────────────────────

  const [product, setProduct] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [lot, setLot] = useState(EMPTY_LOT);
  const [place, setPlace] = useState({ wilayaId: "", commune: "" });
  const [market, setMarket] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]); // [{ file, url }]
  const [voice, setVoice] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [mail, setMail] = useState("");

  const [stepError, setStepError] = useState("");
  const [error, setError] = useState("");
  const [messageBad, setMessageBad] = useState(0); // shakes the message box when a part of it is missing
  const [busy, setBusy] = useState(false); // compressing a picture
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false); // tracking number once sent

  const steps = [
    has("product_id") && "product",
    has("type_complaints") && "problem",
    (has("n_lot") || has("fabrication_date") || has("date_hour")) && "lot",
    has("willaya") && "achat",
  ].filter(Boolean);
  const drawers = useDrawers(steps);

  useEffect(() => setStepError(""), [drawers.activeStep]);

  const wilaya = locations.wilayas.find((w) => w.id === place.wilayaId);
  const dateIso = parseDate(lot.dateText);
  const hour = parseHour(lot.hourText);
  const lotOptional = !STEP_FIELDS.lot.some(isRequired);

  // Values in the shape the obligatory-field rules expect
  const formValues = () => ({
    product,
    selectedTypes,
    n_lot: lot.lot,
    date: has("fabrication_date") ? dateIso : "",
    date_hour: has("date_hour") && dateIso && hour ? `${dateIso} ${hour}` : "",
    compressedLot: lot.photo ? [lot.photo] : [],
    location: { wilaya: wilaya?.text || "", commune: place.commune, market_name: market },
    comment,
    compressedFiles: photos,
    voice_r: voice,
    name,
    phone,
    mail,
  });

  // ── Steps ───────────────────────────────────────────────────────────────────

  const closeStep = (step, summary) => {
    setError("");
    drawers.close(step, summary);
  };

  const validateStep = (step) => {
    const missing = firstMissing(obligatoryUserFields, FIELD_RULES, formValues(), STEP_FIELDS[step]);
    setStepError(missing ? t(missing) : "");
    return !missing;
  };

  const pickProduct = (picked) => {
    if (picked.id !== product?.id) {
      // complaint types depend on the product
      setSelectedTypes([]);
      drawers.reset("problem");
    }
    setProduct(picked);
    closeStep("product", picked);
  };

  useEffect(() => {
    setTypes([]);
    if (!product) return;
    setTypesLoading(true);
    axios
      .get(`${backendLink}consumer/getCompTypeByProduct/${product.id}`)
      .then((response) => setTypes(response.data.data.complaintTypes || []))
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          navigate("/");
        } else {
          console.error("Error fetching data:", error);
        }
      })
      .finally(() => setTypesLoading(false));
  }, [product, navigate]);

  const finishProblem = () => {
    if (!validateStep("problem")) return;
    const names = types.filter((type) => selectedTypes.includes(type.id)).map((type) => localized(type, lang));
    closeStep("problem", names.join(" · "));
  };

  const finishLot = () => {
    if (lot.dateText && !dateIso) return setStepError(t("rcDateBad"));
    if (validateStep("lot")) closeStep("lot", lotSummary(lot, t));
  };

  const skipLot = () => {
    if (lot.photo) URL.revokeObjectURL(lot.photo.url);
    setLot(EMPTY_LOT);
    closeStep("lot", LOT_SKIPPED);
  };

  const finishAchat = () => {
    if (!validateStep("achat")) return;
    closeStep("achat", [...locationNames(locations, place, isRtl), market.trim()].filter(Boolean).join(" · "));
  };

  // Summaries of the finished steps, written in the current language each time (a step only
  // remembers that it is done): switching language translates them too
  const summaries = {
    problem: types.filter((type) => selectedTypes.includes(type.id)).map((type) => localized(type, lang)).join(" · "),
    lot: drawers.done.lot === LOT_SKIPPED ? t("rcSkip") : lotSummary(lot, t),
    achat: [...locationNames(locations, place, isRtl), market.trim()].filter(Boolean).join(" · "),
  };
  const summaryOf = (step) => (drawers.done[step] ? summaries[step] || t("rcNotGiven") : t("rcNotGiven"));

  // ── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending || drawers.firstTodo) return;

    const values = formValues();
    const missing = firstMissing(obligatoryUserFields, FIELD_RULES, values);
    if (missing) {
      // reopen the step the missing field belongs to
      const step = steps.find((s) => STEP_FIELDS[s].includes(missing));
      if (step) drawers.open(step);
      if (MESSAGE_FIELDS.includes(missing)) setMessageBad((n) => n + 1);
      flagField(missing, step ? 400 : 0);
      return setError(t(missing));
    }
    if (mail !== "" && !isValidEmail(mail)) {
      flagField("unvalidMail");
      return setError(t("unvalidMail"));
    }

    const formData = new FormData();
    name && formData.append("full_name", name);
    product?.id && formData.append("product_id", product.id);
    lot.lot && formData.append("n_lot", lot.lot);
    values.date && formData.append("fabrication_date", values.date);
    selectedTypes.forEach((type) => formData.append("type_complaints[]", type));
    values.date_hour && formData.append("date_hour", values.date_hour);
    comment && formData.append("comment", xss(comment));
    values.location.wilaya && formData.append("willaya", values.location.wilaya);
    place.commune && formData.append("commune", place.commune);
    market && formData.append("market_name", market);
    phone && formData.append("phone", phone);
    mail && formData.append("email", mail);
    // single audio file (backend expects voice_r, not voice_r[])
    voice[0] && formData.append("voice_r", voice[0]);
    photos.forEach((photo) => formData.append("img_r[]", asFile(photo)));
    lot.photo && formData.append("img_l", asFile(lot.photo));
    formData.append("channel_id", isStandalone ? 5 : utmSource || 1);
    const deviceId = localStorage.getItem("push_device_id");
    if (deviceId) formData.append("device_id", deviceId);

    setError("");
    setSending(true);
    try {
      await axios.post(`${backendLinkComplaint}consumer/addConsumerComplaint`, formData, {
        withCredentials: true, // Include cookies for CSRF protection
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSent(true);
    } catch (err) {
      console.error("Error sending complaint:", err.response?.data || err);
      setError((err.response?.status === 422 && err.response.data?.message) || t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  const showMedia = has("comment") || has("img_r") || has("voice_r");
  const drawerProps = (step) => ({ drawers, step, sub: t(`rcStep_${step}`), error: stepError, fields: (STEP_FIELDS[step] || []).join(" ") });

  return (
    <PageShell
      formBg
      title={t("rcComplaint")}
      onBack={() => navigate("/")}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      done={sent && <ThanksScreen title={t("rcOkTitle")} text={t("rcOkText")} onHome={() => navigate("/")} />}
    >
      <MetaTags />
      <div className={styles.cardBody}>
        <form noValidate onSubmit={onSubmit}>
          {has("product_id") && (
            <Drawer {...drawerProps("product")} Icon={ReceiptText} summary={product && localized(product, lang)}>
              <Question i18nKey="rcQProduct" center />
              <ProductPicker productsList={productsList} scanner={enabledModules.scannerModule} onPick={pickProduct} />
            </Drawer>
          )}

          {has("type_complaints") && (
            <Drawer {...drawerProps("problem")} Icon={CircleAlert} summary={summaryOf("problem")}>
              <Question i18nKey="rcQProblem" />
              {!product && (
                <div className={styles.hint} style={{ marginBottom: 12 }}>
                  {t("select")}
                </div>
              )}
              <TypeGrid
                types={types}
                loading={typesLoading}
                selected={selectedTypes}
                onToggle={(id) => {
                  setStepError("");
                  if (selectedTypes.includes(id)) return setSelectedTypes([]);
                  // one type on the consumer form: picking it goes straight to the next step
                  setSelectedTypes([id]);
                  const picked = types.find((type) => type.id === id);
                  setTimeout(() => closeStep("problem", localized(picked, lang)), 250);
                }}
              />
              {/* picking a type moves on by itself; the button only stays to skip an optional question */}
              {selectedTypes.length === 0 && !isRequired("type_complaints") && <NextButton onClick={finishProblem} />}
            </Drawer>
          )}

          {steps.includes("lot") && (
            <Drawer {...drawerProps("lot")} Icon={Tag} summary={summaryOf("lot")}>
              <Question i18nKey="rcQLot" />
              <LotFields
                lotImage={product?.group?.img}
                value={lot}
                onChange={(value) => {
                  setStepError("");
                  setLot(value);
                }}
                show={{ lot: has("n_lot"), date: has("fabrication_date") || has("date_hour"), hour: has("date_hour") }}
                isRequired={{
                  lot: isRequired("n_lot"),
                  date: isRequired("fabrication_date") || isRequired("date_hour"),
                  hour: isRequired("date_hour"),
                }}
                lotPlaceholder={t([`lotPlaceholder.${tenantName}`, "lotPlaceholder.default"])}
                lotFilter={LOT_FILTER}
                onBusy={setBusy}
              />
              <NextButton onClick={finishLot} />
              {lotOptional && (
                <button type="button" className={styles.skip} onClick={skipLot}>
                  <PackageX size={18} strokeWidth={1.8} aria-hidden />
                  <span>{t("rcSkip")}</span>
                </button>
              )}
            </Drawer>
          )}

          {has("willaya") && (
            <Drawer {...drawerProps("achat")} Icon={MapPin} summary={summaryOf("achat")}>
              <Question i18nKey="fromWhere" />
              <LocationFields
                locations={locations}
                value={place}
                onChange={(value) => {
                  setStepError("");
                  setPlace(value);
                }}
                isRequired={{ wilaya: isRequired("willaya"), commune: isRequired("commune") }}
              >
                <Field label={t("rcMagasin")} required={isRequired("market_name")} field="market_name">
                  <input
                    type="text"
                    className={styles.input}
                    autoComplete="off"
                    maxLength="25"
                    value={market}
                    onChange={(e) => {
                      setStepError("");
                      setMarket(xss(e.target.value).replace(/[^A-Za-z0-9؀-ۿ\s]/g, "").slice(0, 25));
                    }}
                  />
                </Field>
              </LocationFields>
              <NextButton onClick={finishAchat} />
            </Drawer>
          )}

          {/* Comment, media and contact details: shown once every step is filled */}
          <Reveal open={!drawers.firstTodo}>
            {showMedia && (
              <div style={{ marginTop: 6 }}>
                <MessageLabel
                  required={isRequired("comment") || isRequired("img_r")}
                  count={(comment.trim() ? 1 : 0) + photos.length + voice.length}
                />
                <MessageBox
                  show={{ comment: has("comment"), photos: has("img_r"), voice: has("voice_r") }}
                  comment={comment}
                  setComment={(value) => {
                    setError("");
                    setComment(value);
                  }}
                  photos={photos}
                  setPhotos={setPhotos}
                  setVoice={setVoice}
                  onBusy={setBusy}
                  bad={messageBad}
                />
              </div>
            )}

            {has("full_name") && (
              <>
                {showMedia && <hr className={styles.divider} />}
                <div className={styles.sectionQ} style={{ marginBottom: 4 }}>
                  <Trans i18nKey="rcQContact" components={{ b: <b /> }} />
                </div>
                <div className={styles.hint} style={{ marginTop: 0, marginBottom: 12 }}>
                  {t("rcContactHint")}
                </div>
                <div className={styles.twoCol}>
                  <Field label={t("rcName")} required={isRequired("full_name")} field="full_name">
                    <input
                      type="text"
                      className={styles.input}
                      autoComplete="name"
                      maxLength="25"
                      placeholder={t("rcNamePh")}
                      value={name}
                      onChange={(e) => setName(xss(e.target.value).replace(/[^A-Za-z؀-ۿ\s]/g, ""))}
                    />
                  </Field>
                  <Field label={t("rcPhone")} required={isRequired("phone")} field="phone">
                    <input
                      type="tel"
                      className={classNames(styles.input, styles.ltr)}
                      autoComplete="tel"
                      inputMode="numeric"
                      placeholder="0555123456"
                      value={phone}
                      onChange={(e) => {
                        // always starts with 0, 10 digits at most
                        const digits = toDigits(e.target.value);
                        setPhone((digits && !digits.startsWith("0") ? `0${digits}` : digits).slice(0, 10));
                      }}
                    />
                  </Field>
                </div>
                <Field label={t("rcMail")} required={isRequired("email")} optional={!isRequired("email")} field="email unvalidMail">
                  <input
                    type="email"
                    className={classNames(styles.input, styles.ltr)}
                    autoComplete="email"
                    maxLength="64"
                    value={mail}
                    onChange={(e) => {
                      setError("");
                      setMail(e.target.value.replace(/[^a-zA-Z0-9@._+-]/g, ""));
                    }}
                  />
                </Field>
              </>
            )}
            <ErrorBox>{error}</ErrorBox>
            <button type="submit" className={classNames(styles.submit, { [styles.isSuccess]: sent })} disabled={sending || sent || busy}>
              <Send size={17} strokeWidth={2.2} aria-hidden />
              <span>{sent ? t("rcSent") : t("rcSend")}</span>
            </button>
          </Reveal>
        </form>
      </div>
    </PageShell>
  );
}
